import React from 'react'
import {useSelector} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {ADDONS_TYPES, BILLING_PREFERENCES} from '../utils/constants'
import {
  selectAutoCoupons,
  selectMergedCoupons,
  selectUpgradePaymentPreview,
} from '../app-store/coupon'
import {CartProducts} from './cart-products'
import {ParagraphText} from '@thryvlabs/maverick'
import {ADD_ON} from '../utils/addons-data'
import {currencyFormat, formatPrice} from '../utils/currency-format'

function CartPaymentDetails({
  listOfAddons,
  totalDiscount,
  selectedCountry,
  currentStep,
  setCartProducts,
  cartProducts,
  monthlyBilling,
  semiAnnuallyBilling,
  currentPlanName,
  isNewOrder = true,
  upgradeAccountInfo,
}) {
  const autoCouponInfo = useSelector(selectAutoCoupons, shallowEqual)
  const orderCouponCodes = useSelector(selectMergedCoupons, shallowEqual)
  const upgradePaymentPreview = useSelector(selectUpgradePaymentPreview)
  const discountedOrder = autoCouponInfo?.length > 0
  const getTotalNumberPrice = products => {
    let totalPrice = 0
    for (const i in products) {
      if (typeof products[i].price === 'string') {
        totalPrice +=
          parseFloat(
            products[i].price.replace(',', '').replace(/[^\d.-]/g, ''),
          ).toFixed(2) * 1
      } else {
        totalPrice += products[i].price
      }
    }
    return totalPrice
  }
  const handleInsertProducts = products => {
    const result = []
    products.forEach(product => {
      if (parseFloat(product.price).toFixed(2) > 0) {
        result.push({
          ...product,
          price: currencyFormat(
            product.numericPrice ?? product.price,
            selectedCountry,
          ),
        })
      } else if (
        product.product === 'Credit' ||
        product.product === 'Incentive'
      ) {
        result.push(product)
      }
    })
    return result
  }
  const getListOfAddons = data => {
    let isNewSEOAdded = data.find(item =>
      ['SEO Keyword', 'AEO & SEO', 'AEO & SEO PowerBoost'].includes(
        item.product.trim(' '),
      ),
    )
    if (isNewSEOAdded) {
      data = data.filter(
        item =>
          !['SEO Money Back Guarantee', 'SEO Non-Guaranteed'].includes(
            item.product,
          ),
      )
    }
    return data
  }
  React.useEffect(() => {
    let listOfSelectedAddons = [...listOfAddons]
    listOfSelectedAddons = getListOfAddons(listOfSelectedAddons)
    const discount = totalDiscount
    const designFeeDiscount =
      orderCouponCodes?.find(({code}) => code === 'urldbdesignfee')
        ?.discountAmount || 0
    const selectedPlan = listOfSelectedAddons.find(
      ({type}) => type === ADDONS_TYPES['SELECTED_PLAN'],
    )
    if (
      ADD_ON.add_ons['Marketing Center Plus'].addOnCodes.includes(
        selectedPlan.planCode,
      ) ||
      selectedPlan.product === 'Thryv Leads'
    ) {
      const newListOfSelectedAddons = listOfSelectedAddons.filter(
        ({type}) => type !== ADDONS_TYPES['SELECTED_PLAN'],
      )
      const actualSelectedPlan = {
        type: ADDONS_TYPES['SELECTED_PLAN'],
        product: upgradeAccountInfo.planName,
        price: currencyFormat(upgradeAccountInfo.planPrice, selectedCountry),
        planCode: upgradeAccountInfo.planCode,
        billingPreference: selectedPlan.billingPreference,
      }
      newListOfSelectedAddons.push(actualSelectedPlan)
      listOfSelectedAddons = newListOfSelectedAddons
    }
    const addonsTotal = getTotalNumberPrice(
      listOfSelectedAddons.filter(
        ({type, billingPreference, planCode}) =>
          type === ADDONS_TYPES['ADD_ON'] &&
          billingPreference !== BILLING_PREFERENCES[2] &&
          planCode !== '247support-m2m',
      ),
    )
    const oneTimeTotal = getTotalNumberPrice(
      listOfSelectedAddons.filter(
        ({type, billingPreference}) =>
          type === ADDONS_TYPES['ONE_TIME'] ||
          billingPreference === BILLING_PREFERENCES[2],
      ),
    )
    const subtotalNum = upgradePaymentPreview
      ? parseFloat(upgradePaymentPreview.total)
      : getTotalNumberPrice(
          listOfSelectedAddons.filter(
            ({type, billingPreference}) =>
              type === ADDONS_TYPES['ONE_TIME'] ||
              billingPreference === BILLING_PREFERENCES[2],
          ),
        ) +
        getTotalNumberPrice(
          listOfSelectedAddons.filter(
            ({type, billingPreference}) =>
              type === ADDONS_TYPES['ADD_ON'] &&
              billingPreference !== BILLING_PREFERENCES[2],
          ),
        ) +
        getTotalNumberPrice(
          listOfSelectedAddons.filter(
            ({type, billingPreference}) =>
              type === ADDONS_TYPES['SELECTED_PLAN'] && billingPreference,
          ),
        ) +
        getTotalNumberPrice(
          listOfSelectedAddons.filter(
            ({type, product}) =>
              type === ADDONS_TYPES['SELECTED_PLAN'] &&
              product === 'Onboarding Fee',
          ),
        ) -
        discount +
        designFeeDiscount +
        (upgradeAccountInfo?.balanceInfo?.availableCreditAmount || 0)
    const semiAnnuallyTotal = getTotalNumberPrice(
      listOfSelectedAddons.filter(
        ({billingPreference}) => billingPreference === BILLING_PREFERENCES[1],
      ),
    )
    const monthlyTotal = getTotalNumberPrice(
      listOfSelectedAddons.filter(
        ({billingPreference}) => billingPreference === BILLING_PREFERENCES[0],
      ),
    )
    const toDisplayPrice = n =>
      n
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const formattedAddons = [
      {
        product: 'Addons',
        type: ADDONS_TYPES['ADD_ON'],
        price: addonsTotal.toFixed(2),
        numericPrice: addonsTotal,
      },
      {
        product: 'One-time',
        type: ADDONS_TYPES['ADD_ON'],
        price: oneTimeTotal.toFixed(2),
        numericPrice: oneTimeTotal,
      },
      {
        product: isNewOrder ? 'Subtotal' : 'Due Today',
        price: toDisplayPrice(subtotalNum),
        numericPrice: subtotalNum,
        highlighted: true,
      },
      {
        product: 'Due Semi-Annually',
        type: ADDONS_TYPES['SELECTED_PLAN'],
        price: toDisplayPrice(semiAnnuallyTotal),
        numericPrice: semiAnnuallyTotal,
        highlighted: true,
      },
      {
        product: 'Credit',
        price: formatPrice(
          upgradeAccountInfo?.balanceInfo?.availableCreditAmount || 0,
          1,
          '',
          selectedCountry,
        ),
        highlighted: true,
      },
      {
        product: 'Due Monthly',
        type: ADDONS_TYPES['SELECTED_PLAN'],
        price: toDisplayPrice(monthlyTotal),
        numericPrice: monthlyTotal,
        // price: (
        //   Number(
        //     getTotalPrice(
        //       listOfSelectedAddons.filter(
        //         ({billingPreference}) =>
        //           billingPreference === BILLING_PREFERENCES[0],
        //       ),
        //     ),
        //   ) + upgradeAccountInfo?.balanceInfo?.availableCreditAmount || 0
        // )
        //   .toFixed(2)
        //   .toString()
        //   .replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        highlighted: true,
      },
    ]
    const supportFee = listOfAddons.find(
      ({planCode}) => planCode === '247support-m2m',
    )
    if (supportFee) {
      const supportFeeItem = {
        product: 'Support Fee',
        price: supportFee.price.replace('$', ''),
        highlighted: false,
      }
      const dueTodayIndex = formattedAddons.findIndex(
        ({product}) =>
          product === 'Due Today' ||
          product === 'Subtotal' ||
          product === 'Credit',
      )
      formattedAddons.splice(dueTodayIndex, 0, supportFeeItem)
    }
    if (discount > 0) {
      formattedAddons.splice(2, 0, {
        product: 'Incentive',
        elements: orderCouponCodes,
        highlighted: false,
      })
    }
    setCartProducts([
      ...listOfSelectedAddons.filter(
        ({type}) => type === ADDONS_TYPES['SELECTED_PLAN'],
      ),
      ...handleInsertProducts(formattedAddons),
    ])
  }, [
    listOfAddons,
    totalDiscount,
    autoCouponInfo,
    orderCouponCodes,
    setCartProducts,
    isNewOrder,
    upgradeAccountInfo?.planCode,
    upgradeAccountInfo?.planName,
    upgradeAccountInfo?.planPrice,
    upgradeAccountInfo?.balanceInfo?.availableCreditAmount,
    upgradePaymentPreview,
  ])

  return (
    <>
      <CartProducts
        listOfProducts={cartProducts || []}
        selectedCountry={selectedCountry}
        currentStep={currentStep}
        monthlyBilling={monthlyBilling}
        semiAnnuallyBilling={semiAnnuallyBilling}
        currentPlanName={currentPlanName}
        discountedOrder={discountedOrder}
        currentPlanPrice={upgradeAccountInfo?.planPrice}
      />
      {(selectedCountry === 'AU' || selectedCountry === 'aus') &&
        currentStep !== 7 && (
          <div>
            <ParagraphText
              variant="sm"
              className="font-weight-bold mt-4
          "
            >
              *All plans include GST except SEO.
            </ParagraphText>
          </div>
        )}
    </>
  )
}

export {CartPaymentDetails}
